import type { Subscription } from "rxjs";
import { zip as observableZip } from "rxjs";

import { cloneDeep, isEmpty } from "lodash";
import { finalize } from "rxjs/operators";

import type { IComponentController, IScope } from "angular";

import type {
    ISiteEquipmentRepository,
    ISiteFunctionalitiesRepository,
    ISitesRepository,
} from "App/Modules/Repositories";
import type { IDefaultNamingService, IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";

import type {
    IConfigurationGroup,
    IConfigurationGroupDetection,
    IFunctionality,
    IFunctionalityRecording,
    IResponse,
    IResponseWithLoading,
    ISiteEquipmentSlim,
    ISiteEquipmentWithCoverages,
    IVideoStream,
} from "App/Modules/Models";
import { ConfigurationGroupType, EquipmentClass, VideoStreamFunctions } from "App/Modules/Models";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditRecordingFunctionalityComponentController implements IComponentController {
    public static $inject = [
        "$scope",
        "mmSiteFunctionalitiesRepository",
        "mmSitesRepository",
        "mmSiteEquipmentRepository",
        "irDefaultNamingService",
    ];

    public originalFunctionality: IFunctionality;

    public functionality: IFunctionality;

    public siteEquipment: ISiteEquipmentWithCoverages;

    public configurationGroups: IConfigurationGroup[];

    public computerSiteEquipment: ISiteEquipmentSlim[];

    public videoStreams: IVideoStream[];

    public typeName: string;

    public saveResponse: IResponseWithLoading<IFunctionality>;

    public errorResponse: IResponse<IFunctionalityRecording | IConfigurationGroupDetection[]>;

    public deleteResponse: IResponseWithLoading<void>;

    public resolve: IFormContainerResolve<
        [IFunctionality, string, boolean],
        ISiteEquipmentWithCoverages,
        IFunctionality
    >;

    public isDataLoading = true;

    public subscription: Subscription;

    public isEditable: boolean;

    constructor(
        private readonly $scope: IScope,
        private readonly siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private readonly sitesRepository: ISitesRepository,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly defaultNamingService: IDefaultNamingService,
    ) {}

    public $onInit() {
        this.siteEquipment = this.resolve.bindings;
        const [fn, siteId, isEditable]: [IFunctionality, string, boolean] = this.resolve.formData;

        this.isEditable = isEditable;

        this.typeName = fn.typeName;

        const mdFunctionality$ = this.siteFunctionalitiesRepository.getFunctionality<IFunctionalityRecording>(
            siteId,
            fn.id,
        );

        const configurationGroups$ = this.sitesRepository.getConfigurationGroups(
            siteId,
            ConfigurationGroupType.Recording,
        );

        const computerSiteEquipment$ = this.sitesRepository.getSiteEquipments(siteId, EquipmentClass.Computer);

        let videoStreamEquipmentId = this.siteEquipment.id;
        if (this.siteEquipment.masterSiteEquipment) {
            videoStreamEquipmentId = this.siteEquipment.masterSiteEquipment.id;
        }

        const videoStreams$ = this.siteEquipmentRepository.getVideoStreamsForSiteEquipment(
            siteId,
            videoStreamEquipmentId,
        );

        this.subscription = observableZip(configurationGroups$, mdFunctionality$, computerSiteEquipment$, videoStreams$)
            .pipe(
                finalize(() => {
                    this.isDataLoading = false;
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                ([configurationGroups, functionality, computerSiteEquipment, videoStreams]) => {
                    this.configurationGroups = configurationGroups.data as IConfigurationGroupDetection[];
                    this.configurationGroups.forEach((cg) => this.defaultNamingService.nameConfigurationGroup(cg));
                    this.videoStreams = VideoStreamFunctions.combineVideoStreamLists(
                        videoStreams.data,
                        this.siteEquipment,
                    );
                    this.computerSiteEquipment = computerSiteEquipment.data;
                    this.functionality = functionality.data;
                    this.originalFunctionality = cloneDeep(this.functionality);
                },
                (err: IResponse<IFunctionalityRecording | IConfigurationGroupDetection[]>) => {
                    this.errorResponse = err;
                },
            );
    }

    public $onDestroy() {
        this.subscription.unsubscribe();
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editFunctionality() {
        const patchObject = PatchDelta.difference(this.originalFunctionality, this.functionality);

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.siteFunctionalitiesRepository.patchFunctionality(
                    this.resolve.formData[1],
                    this.originalFunctionality.id,
                    this.functionality.type,
                    patchObject,
                ),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (saveResponse: IResponseWithLoading<IFunctionalityRecording>) => {
                        this.saveResponse = saveResponse;
                        if (!saveResponse.isLoading && saveResponse.data) {
                            this.resolve.options.onOk(saveResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.saveResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public deleteFunctionality() {
        extendWithLoadingAndFail(
            this.siteFunctionalitiesRepository.deleteFunctionality(this.resolve.formData[1], this.functionality.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;
                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (errorResponse) => {
                    this.deleteResponse = errorResponse;
                },
            );
    }
}
